import React, { FocusEvent } from 'react';
import Select, {
  components,
  StylesConfig,
  OptionProps,
  SingleValueProps,
  mergeStyles,
} from 'react-select';
import { FieldInputProps, FieldMetaState } from 'react-final-form';

import AppStorage from '../../Storage/Storage';
import {
  translate,
} from '../../utils/utils';
import Labels from '../../enums/Labels';
import { CSSObjectWithLabel } from 'react-select/dist/declarations/src/types';

type Options = {
  value: string;
  label: string;
  icon?: string;
};

export type ValueTypeEvent = {
  value: string;
};

type Props = {
  changeHandler?(event: ValueTypeEvent, e: { action: string }): void;
  blurHandler?(e: FocusEvent<HTMLElement>): void;
  focusHandler?(e: FocusEvent<HTMLElement>): void;
  id?: string;
  label: Labels;
  disabled?: boolean;
  value?: string;
  options: Options[];
  placeholder: Labels;
  isIconOption?: boolean;
  isForm?: boolean;
  customStyles: StylesConfig;
  isRequired: boolean;
  isSearchable: boolean;
  meta?: FieldMetaState<string[]>;
  input?: FieldInputProps<string[]>;
  isError: boolean;
} & Partial<DefaultProps>;

const defaultProps = {
  label: '',
  disabled: false,
  placeholder: '',
  isIconOption: false,
  isForm: false,
  isRequired: false,
  isSearchable: false,
  customStyles: {},
  isError: false,
};

type DefaultProps = Readonly<typeof defaultProps>;

class SelectComponent extends React.PureComponent<Props> {
  private readonly storage: AppStorage;

  static defaultProps: DefaultProps = defaultProps;

  constructor(props: Props) {
    super(props);
    this.storage = AppStorage.getInstance();
  }

  render(): React.ReactElement {
    const {
      id,
      changeHandler,
      blurHandler,
      focusHandler,
      options,
      disabled,
      value,
      placeholder,
      isIconOption,
      customStyles,
      input,
      isSearchable,
      isError,
    } = this.props;

    const { Option, SingleValue } = components;

    const IconOption = (props: OptionProps<Options>): React.ReactElement => (
      <Option {...props}>
        {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-member-access */}
        <img src={props.data.icon} alt={props.data.label} />
      </Option>
    );

    const SingleValueComponent = (props: SingleValueProps<Options>): React.ReactElement => (
      <SingleValue {...props}>
        <img src={props.data.icon} alt={props.data.label} />
      </SingleValue>
    );

    const selectedValue = options.filter((option: Options) => option.value === value)[0];

    const restProps = isIconOption ? {
      components: {
        Option: IconOption,
        SingleValue: SingleValueComponent,
      },
    } : null;

    const getBGColor = (provided: CSSObjectWithLabel, state: { isFocused: boolean, isSelected: boolean }): string => {
      if (state.isFocused && !state.isSelected) {
        return 'var(--primary-color)';
      }
      if (state.isSelected) {
        return 'var(--secondary-color)';
      }
      return provided.backgroundColor as string;
    };

    const defaultStyles: StylesConfig = {
      indicatorSeparator: (provided: CSSObjectWithLabel) => ({
        ...provided,
        display: 'none',
      }),
      control: (provided: CSSObjectWithLabel, state) => ({
        ...provided,
        minHeight: 'initial',
        padding: '0',
        backgroundColor: 'transparent !important',
        fontFamily: 'var(--body-font)',
        border: `1px solid var(${isError ? '--error' : '--translucent-light-blue'}) !important`,
        fontSize: '1rem',
        lineHeight: 'normal',
        width: '100%',
        boxShadow: 'none',
        appearance: 'none',
        borderRadius: '3px',
        height: '36px',
        cursor: 'pointer',
        '[class*="singleValue"]': {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          color: isSearchable && state.menuIsOpen ? 'var(--grey)' : 'var(--dark-blue)',
        },
        // TODO: Remove color overrides as soon as https://github.com/facebook/create-react-app/issues/7185 is merged
        '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
          borderColor: 'rgba(0, 138, 201, .6) !important',
          fontFamily: 'Sun, sans-serif',
          '[class*="singleValue"]': {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            color: isSearchable && state.menuIsOpen ? 'rgb(201, 201, 201)' : 'rgb(0, 48, 86)',
          },
        },
      }),
      singleValue: (provided: CSSObjectWithLabel) => ({
        ...provided,
        top: isSearchable ? '35%' : '40%',
      }),
      input: (provided:CSSObjectWithLabel) => ({
        ...provided,
        marginTop: '8px',
        paddingTop: '0',
      }),
      option: (provided:CSSObjectWithLabel, state) => ({
        ...provided,
        cursor: 'pointer',
        backgroundColor: getBGColor(provided, state),
      }),
      indicatorsContainer: (provided:CSSObjectWithLabel) => ({
        ...provided,
        position: 'relative',
        height: '100%',
        right: '7px',
        top: '-3px',
      }),
      dropdownIndicator: (provided:CSSObjectWithLabel) => ({
        ...provided,
        position: 'relative',
        padding: '0 !important',
        svg: {
          display: 'none',
        },
        ':after': {
          content: '""',
          width: '15px',
          height: '15px',
          display: 'inline-block',
          background: 'url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABgAAAAYCAYAAADgdz34AAAACXBIWXMAAAsTAAALEwEAmpwYAAABo0lEQVRIie2QMUhbURSGv3MjwZoWoaMOHVpooUO2CEEFKUkGLXYPLt1tg/CwTn2bvmspYufSFnQThG6SggqieU8Xh2xdCsXRDk1siL6cDtYQTcWkzdLiN97z/985XLjmv0ewhXfUZIEXA/sdNc/5cYzmDCIlDGvYvbsdk89u38OwBvLdcOdLDnQDwjzeVt9fy72tPiKRPCrrHCVyAoBbjBIrfUTpJ6wOMzP07Y/kr7dvcxLZBL5ydHMc92FV6sP5/Rha+YRQg+40Trzclvy0nwdAulNnfVMPOPEyXeEoSi9aWcEtRluWu8UoWlkBbtEVjjUeZ84Fp5KH6HEaeEBP6T2uGq7CVcON8gfgPnqcYSp52DhuFkwPHhCGKeARPbuLqEpT5gxVIea/QXSEMEwzPXhwMfL7C2eSn0EyoBPY4OWlC+Z3XVSy1MicdtrlVTCM9X9g/clmefDs12yoffF50WOsX8ULsvU3L8hi/SrWH7uqfvn/NuIVJhB5C/oEEUFZReQpTmKpMwsAbOE5GA9UEHFwEostd1tfsrOM3VnuvPiaf5qfsq+UrWHhvysAAAAASUVORK5CYII=) no-repeat 0 center',
          backgroundSize: '70%',
          position: 'absolute',
          right: '0',
          top: '0',
        },
      }),
    };

    return (
      <Select
        {...input}
        id={id}
        className="react-select-container"
        classNamePrefix="react-select"
        options={options}
        disabled={disabled}
        value={selectedValue}
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        onChange={changeHandler}
        onBlur={blurHandler}
        onFocus={focusHandler}
        placeholder={translate(placeholder, this.storage.getLocale)}
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        styles={mergeStyles(defaultStyles, customStyles)}
        isSearchable={isSearchable}
        {...restProps}
      />
    );
  }
}

export default SelectComponent;
